import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Select from 'react-select';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import './Customer.css';

const CustomerEdit = ({ match }) => {
    const history = useHistory();
    const { id } = useParams();
    const { isAuthenticated, companyId, userId, token, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, token, logout]);

    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [roomName, setRoomName] = useState([]);
    const [roomTypeOptions, setRoomTypeOptions] = useState([]);
    const [selectedRoomType, setSelectedRoomType] = useState(null);
    const [customRentAmount, setCustomRentAmount] = useState('');
    const [cardTypeOptions, setCardTypeOptions] = useState([]);
    const [selectedCardType, setSelectedCardType] = useState(null);

    useEffect(() => {
        fetchData('CustomerApi', `/CustomerEdit/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Customer Details:', error);
            });
    }, [id]);

    useEffect(() => {
        const fetchRoomTypes = async () => {
            try {
                const response = await fetchData('CustomerApi', `/RommDetails/${companyId}`);
                if (response.responseCode === '1111') {
                    const options = response.response_data.map((room) => ({
                        value: room.ID,
                        label: room.Name,
                    }));
                    setRoomName(response.response_data);
                    setRoomTypeOptions(options);
                }
            } catch (error) {
                console.error('Error fetching Room Types:', error);
            }
        };
        fetchRoomTypes();
    }, [companyId]);

    const handleRoomChange = (selectedOption) => {
        setSelectedRoomType(selectedOption);
        if (selectedOption) {
            const selectedRoomData = roomName.find((room) => room.ID === selectedOption.value);
            if (selectedRoomData) {
                setCustomRentAmount(selectedRoomData.RentAmount || '');
            }
        }
    };

    useEffect(() => {
        const fetchCardTypes = async () => {
            try {
                const response = await fetchData('CustomerApi', `/${companyId}/${companyId}`, token);
                const options = response.map((Card) => ({
                    value: Card.id,
                    label: Card.cardName,
                }));
                setCardTypeOptions(options);
            } catch (error) {
                console.error('Error fetching Card Types:', error);
            }
        };
        fetchCardTypes();
    }, [companyId, token]);

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const currentDate = new Date(dateString);
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        if (!formData.name) {
            setErrorMessage('Name is required!');
            setIsLoading(false);
            return;
        }

        const updatedFormData = {
            ...formData,
            RoomId: selectedRoomType?.value,
            IdCardId: selectedCardType?.value,
            RoomRentCustomer: customRentAmount || 0,
            UpdatedBy: userId,
        };

        try {
            await updateData('CustomerApi', `${id}`, updatedFormData, token);
            setSuccessMessage('Customer updated successfully!');
            history.push('/CustomerIndex');
        } catch (error) {
            setErrorMessage('Failed to update customer.');
            setIsLoading(false);
        }
    };

    return (
        <div className="form-container">
            <div className="form-card">
                <div className="form-header">
                    <h1 className="form-title">Edit Customer</h1>
                    <div className="form-actions">
                        <Link to="/CustomerIndex" className="btn-back">
                            <ArrowBackIcon fontSize="small" />
                            Back to List
                        </Link>
                        <button className="btn-save" onClick={handleSubmit} disabled={isLoading}>
                            {isLoading ? <CircularProgress size={18} color="inherit" /> : <><SaveIcon fontSize="small" />Update</>}
                        </button>
                    </div>
                </div>

                {errorMessage && <div className="message error">{errorMessage}</div>}
                {successMessage && <div className="message success">{successMessage}</div>}

                <div className="form-body">
                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Name *</label>
                            <input className="form-input" name="name" value={formData.name || ''} onChange={handleChange} type="text" placeholder="Enter full name" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Contact No. 1 *</label>
                            <input className="form-input" name="contactNumber1" value={formData.contactNumber1 || ''} onChange={handleChange} type="text" placeholder="Primary contact" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Contact No. 2</label>
                            <input className="form-input" name="contactNumber2" value={formData.contactNumber2 || ''} onChange={handleChange} type="text" placeholder="Secondary contact" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Email</label>
                            <input className="form-input" name="email" value={formData.email || ''} onChange={handleChange} type="email" placeholder="Email address" />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Address Line 1</label>
                            <input className="form-input" name="addressLine1" value={formData.addressLine1 || ''} onChange={handleChange} type="text" placeholder="Street address" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Address Line 2</label>
                            <input className="form-input" name="adressLine2" value={formData.adressLine2 || ''} onChange={handleChange} type="text" placeholder="Apartment, suite" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Location</label>
                            <input className="form-input" name="location" value={formData.location || ''} onChange={handleChange} type="text" placeholder="City / Area" />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group form-select">
                            <label className="form-label">ID Proof</label>
                            <Select value={selectedCardType || (formData.cardType ? { value: formData.cardType, label: formData.cardType } : null)} onChange={setSelectedCardType} options={cardTypeOptions} placeholder="Select ID type" menuPortalTarget={document.body} styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Proof Number</label>
                            <input className="form-input" name="proofNo" value={formData.proofNo || ''} onChange={handleChange} type="text" placeholder="ID number" />
                        </div>
                        <div className="form-group form-select">
                            <label className="form-label">Room</label>
                            <Select value={selectedRoomType || (formData.roomName ? { value: formData.roomName, label: formData.roomName } : null)} onChange={handleRoomChange} options={roomTypeOptions} placeholder="Select room" menuPortalTarget={document.body} styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }} />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Rent Amount</label>
                            <input className="form-input" value={customRentAmount || formData.rentAmount || ''} onChange={(e) => setCustomRentAmount(e.target.value)} type="text" placeholder="Monthly rent" style={{ textAlign: 'right' }} />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Date of Joining</label>
                            <input className="form-input" name="dateOfJoining" value={formatDateForInput(formData.dateOfJoining)} onChange={handleChange} type="date" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Date of Leaving</label>
                            <input className="form-input" name="dateOfLeaving" value={formatDateForInput(formData.dateOfLeaving)} onChange={handleChange} type="date" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Rent Date</label>
                            <input className="form-input" name="rentDate" value={formatDateForInput(formData.rentDate)} onChange={handleChange} type="date" />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Caution Deposit</label>
                            <input className="form-input" name="cautionDeposite" value={formData.cautionDeposite || ''} onChange={handleChange} type="text" placeholder="Deposit amount" style={{ textAlign: 'right' }} />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group full-width">
                            <label className="form-label">Customer Remarks</label>
                            <input className="form-input" name="customerRemark" value={formData.customerRemark || ''} onChange={handleChange} type="text" placeholder="Any additional notes..." />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerEdit;
